// ── Notification Types ─────────────────────────────────────────────────────────
//
// Domain types and API contracts for notifications.
// Notifications are created internally when users are assigned to cards,
// mentioned, or when board changes occur.
//
// read/email_sent default to false on create.
// read_at is set when read transitions to true.

import { z } from "zod";
import { Card } from "./card.type";

// ── NotificationType ───────────────────────────────────────────────────────────

export const NotificationType = {
  CARD_ASSIGNED: "card_assigned",
  BOARD_CHANGE: "board_change",
  MENTION: "mention",
} as const;

export type NotificationType = (typeof NotificationType)[keyof typeof NotificationType];

// ── Notification ───────────────────────────────────────────────────────────────

export const Notification = z.object({
  id: z.string().describe("Unique resource identifier"),
  user_id: z.string().min(1).describe("ID of the user who receives the notification"),
  type: z
    .enum(Object.values(NotificationType) as [string, ...string[]])
    .describe("Notification type: card_assigned | board_change | mention"),
  title: z.string().min(1).describe("Short notification title"),
  message: z.string().min(1).describe("Full notification message"),
  read: z.boolean().describe("Whether the notification has been read"),
  read_at: z.coerce
    .date()
    .nullable()
    .optional()
    .describe("Timestamp when the notification was read"),
  card_id: z.string().optional().describe("Related card ID, if applicable"),
  board_id: z.string().optional().describe("Related board ID, if applicable"),
  team_id: z.string().min(1).describe("Owning team ID"),
  team_name: z.string().optional().describe("Team display name"),
  email_sent: z.boolean().describe("Whether an email was sent for this notification"),
  created_at: z.coerce.date().describe("Creation timestamp"),
  updated_at: z.coerce.date().describe("Last update timestamp"),
  // ── Computed embed fields — present only when ?embed= is requested ──────────
  /** NOT stored. Resolved inline by the repo aggregation pipeline when ?embed=card. */
  card: Card.partial().optional().describe("Embedded card — present only when ?embed=card"),
});

export type Notification = z.infer<typeof Notification>;

// ── NotificationCreate ─────────────────────────────────────────────────────────

export const NotificationCreate = Notification.omit({
  id: true,
  created_at: true,
  updated_at: true,
  // embed field — computed by the repo, never sent by the client
  card: true,
}).extend({
  read: z.boolean().optional().default(false),
  email_sent: z.boolean().optional().default(false),
  read_at: z.coerce.date().nullable().optional(),
});

export type NotificationCreate = z.infer<typeof NotificationCreate>;

// ── NotificationUpdate ─────────────────────────────────────────────────────────
// Only read state can be updated after creation.

export const NotificationUpdate = z.object({
  read: z.boolean().optional().describe("Mark notification as read/unread"),
  read_at: z.coerce
    .date()
    .nullable()
    .optional()
    .describe("Timestamp when the notification was read"),
});

export type NotificationUpdate = z.infer<typeof NotificationUpdate>;

// ── MarkAllReadBody ────────────────────────────────────────────────────────────

export const MarkAllReadBody = z.object({
  user_id: z
    .string()
    .min(1)
    .describe("User ID — marks all unread notifications for this user as read"),
});

export type MarkAllReadBody = z.infer<typeof MarkAllReadBody>;
